import { computed, Ref } from "vue";

import { useMoveSectionMutation } from "@/composables/kili/sections/mutations/use-move-section-mutation";
import { usePatchSectionMutation } from "@/composables/kili/sections/mutations/use-patch-section-mutation";
import { V4BMutationOpts } from "@/composables/tanstack/use-generic-mutation";
import { SiteSectionId, V4HSiteId } from "@/data/common/ids";
import { MoveSectionRequest, PatchSiteSectionRequest } from "@/domains/v4b/site-section/site-section.types";

export function useUpdateSectionMutation(
  siteId: Ref<V4HSiteId>,
  sectionId: Ref<SiteSectionId | undefined>,
  opts?: V4BMutationOpts,
) {
  const { mutateAsync: patchSiteSection, isPending: isPatchingSiteSection } = usePatchSectionMutation(
    siteId,
    sectionId,
    opts,
  );
  const { mutateAsync: moveSection, isPending: isMovingSection } = useMoveSectionMutation(siteId, sectionId);

  const isPending = computed<boolean>(() => isPatchingSiteSection.value || isMovingSection.value);

  async function updateSectionDetails(requests: {
    patchRequest?: PatchSiteSectionRequest;
    moveRequest?: MoveSectionRequest;
  }): Promise<boolean> {
    const { patchRequest, moveRequest } = requests;
    const mutations: Array<() => Promise<unknown>> = [];

    if (patchRequest) {
      mutations.push(() => patchSiteSection(patchRequest));
    }

    if (moveRequest) {
      mutations.push(() => moveSection(moveRequest));
    }

    let hasFailure = false;

    // Ideally we  avoid triggering `n` calls at the same time
    for (const makePromise of mutations) {
      await makePromise().catch((err) => {
        console.error(err);
        hasFailure = true;
      });
    }

    if (hasFailure) {
      return false;
    }

    return true;
  }

  return {
    isPending,
    updateSectionDetails,
  };
}
